import React, { useEffect, useRef, useState } from "react";
import { ImageOff } from "lucide-react";

/**
 * Classic 2000s dial-up image loader:
 *   - shows a chunky "Loading image..." placeholder while fetching
 *   - reveals the image top-to-bottom with a scanline sweep (like
 *     progressive JPEGs on a 56k modem)
 *   - on error, shows the iconic red-X broken image placeholder
 *     instead of silently disappearing
 *
 * Usage:
 *   <RetroImage src={url} alt="" className="w-full h-full object-cover" />
 *
 * Tip: wrap in an element with a fixed aspect ratio for best effect.
 */
export default function RetroImage({
  src,
  alt = "",
  className = "",
  wrapperClassName = "",
  minDelay = 180, // floor loading delay so the sweep is visible even on fast networks
}) {
  const [status, setStatus] = useState("loading"); // loading | loaded | error
  const [revealed, setRevealed] = useState(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    setStatus("loading");
    setRevealed(false);
    startRef.current = Date.now();
  }, [src]);

  const handleLoad = () => {
    const elapsed = Date.now() - startRef.current;
    const remaining = Math.max(0, minDelay - elapsed);
    setTimeout(() => {
      setStatus("loaded");
      // kick off sweep on next frame
      requestAnimationFrame(() => setRevealed(true));
    }, remaining);
  };

  const handleError = () => {
    setStatus("error");
  };

  return (
    <div
      data-testid="retro-image"
      className={`relative bg-black overflow-hidden ${wrapperClassName}`}
    >
      {/* Placeholder (shown until loaded; always rendered under so sweep reveals image) */}
      {status !== "error" && (
        <div
          aria-hidden
          className={`absolute inset-0 flex flex-col items-center justify-center text-center select-none transition-opacity ${status === "loaded" ? "opacity-0 duration-500" : "opacity-100"}`}
          style={{
            background:
              "repeating-linear-gradient(45deg, #1A1A24 0 6px, #22222E 6px 12px)",
            color: "#B6FF00",
            fontFamily: '"Share Tech Mono", monospace',
          }}
        >
          <div className="scan-spinner mb-1" />
          <div className="text-[9px] tracking-widest">LOADING...</div>
          <div className="text-[8px] opacity-70">56k modem</div>
        </div>
      )}

      {/* Actual image */}
      {status !== "error" && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className={`${className} ${status === "loaded" ? "block" : "invisible"}`}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {/* Dial-up scanline sweep overlay */}
      {status === "loaded" && !revealed && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 45%, rgba(182,255,0,0.35) 50%, rgba(0,0,0,0.85) 51%, rgba(0,0,0,0.85) 100%)",
            animation: "dialup-sweep 0.6s linear forwards",
          }}
        />
      )}

      {/* Error state — classic IE "broken image" icon */}
      {status === "error" && (
        <div
          aria-hidden
          className="absolute inset-0 flex flex-col items-center justify-center bg-white text-[#8B0A14] select-none"
          style={{ fontFamily: '"Share Tech Mono", monospace' }}
        >
          <div className="bevel-out bg-white p-1 mb-1">
            <div
              className="w-7 h-7 flex items-center justify-center"
              style={{
                background: "#E81123",
                color: "white",
                fontWeight: "bold",
                fontFamily: "Arial, sans-serif",
                fontSize: 16,
                lineHeight: 1,
                border: "1px solid #5B0207",
              }}
            >
              ×
            </div>
          </div>
          <div className="flex items-center gap-1 text-[9px]">
            <ImageOff size={9} />
            Image not found
          </div>
        </div>
      )}
    </div>
  );
}
